import { useQuery } from '@tanstack/react-query';
import { UserKeys, getAllGroupTemplates } from '../api/users-api';
import { GroupTemplate } from '../../types';
import { Messages } from '../utils/message-constants';
import { sortGroupTemplatesByName } from './group-settings.config';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';

interface GroupTemplateAutocompleteProps {
  value: GroupTemplate | null;
  onChange: (value: GroupTemplate | null) => void;
  sx?: SxProps<Theme>;
}

export default function GroupTemplateAutocomplete({
  value,
  onChange,
  sx,
}: GroupTemplateAutocompleteProps) {
  const theme = useTheme();

  const { data: groupTemplates, isLoading: templatesLoading } = useQuery({
    queryKey: [UserKeys.GroupTemplates],
    queryFn: () => getAllGroupTemplates(),
    select: sortGroupTemplatesByName,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('group templates'),
    },
  });

  return (
    <Autocomplete
      sx={{ width: 400, ...sx }}
      options={groupTemplates ?? []}
      loading={templatesLoading}
      getOptionLabel={(option) => option.groupTemplateName}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Group Template"
          sx={{
            '& .MuiOutlinedInput-root': {
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'stretch',
              gap: 1,
              padding: '8px 8px 8px 12px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.semantic.button.secondary.border,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.semantic.button.secondary.border,
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '0 !important',
              color: theme.palette.semantic.button.secondary.text,
            },
            '& .MuiInputLabel-root': {
              color: theme.palette.semantic.button.secondary.text,
            },
            '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
              top: '50%',
              transform: 'translate(14px, -50%) scale(1)',
            },
          }}
        />
      )}
    />
  );
}
